// Data access layer: every function here reads from Supabase and shapes
// the result to match the exact TS interfaces already used by the pages
// (defined in src/mocks/*). This means the components never need to know
// whether they're looking at live data or the mock fallback — see
// src/hooks/useSupabaseData.ts for how the two are stitched together.
//
// Table/column names are documented in supabase/schema.sql — that file is
// the source of truth for what needs to exist in the Supabase project.

import { supabase } from "@/lib/supabase";
import type { Season, RegisterProduct } from "@/mocks/registerProducts";
import type { PracticeSlot } from "@/mocks/practiceSchedule";
import type { PlaybookResources, YouTubeVideo } from "@/mocks/playbookResources";
import type { HallOfFamePlayer } from "@/mocks/formerPlayers";

export interface ActiveSeason {
  name: Exclude<Season, "evergreen">;
  dateRange: string;
  heroImage: string;
}

/** "225" -> 225, "Pricing announced soon" -> unchanged. Mirrors how
 *  ProductCard already renders `product.price` (see register/page.tsx). */
function parsePrice(value: string | number | null | undefined): number | string {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") return value;
  return /^\d+(\.\d+)?$/.test(value.trim()) ? Number(value) : value;
}

export async function getActiveSeason(): Promise<ActiveSeason> {
  const { data, error } = await supabase
    .from("seasons")
    .select("name, date_range, hero_image_url")
    .eq("is_active", true)
    .limit(1)
    .single();

  if (error || !data) {
    throw error ?? new Error("No active season found in Supabase");
  }

  return {
    name: data.name,
    dateRange: data.date_range,
    heroImage: data.hero_image_url,
  };
}

export async function getRegisterProducts(): Promise<RegisterProduct[]> {
  const { data, error } = await supabase
    .from("register_products")
    .select(
      "id, name, price, date_range, description, season, cta_link, cta_label, badge, image_url, cta_style, accent, price_note, footnote, sort_order"
    )
    .order("sort_order", { ascending: true });

  if (error) throw error;

  return (data ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    price: parsePrice(row.price),
    dateRange: row.date_range,
    description: row.description,
    season: row.season as Season,
    ctaLink: row.cta_link,
    ctaLabel: row.cta_label ?? undefined,
    accent: row.accent ?? undefined,
    badge: row.badge ?? undefined,
    imageUrl: row.image_url ?? undefined,
    ctaStyle: (row.cta_style as "primary" | "secondary" | undefined) ?? undefined,
    priceNote: row.price_note ?? undefined,
    footnote: row.footnote ?? undefined,
  }));
}

export async function getPracticeSchedule(): Promise<PracticeSlot[]> {
  // Only pulls slots tagged for the currently active season (or "evergreen"
  // slots like open private-lesson windows), same rule as Register.
  const season = await getActiveSeason().catch(() => null);

  let query = supabase
    .from("practice_schedule")
    .select("day, team, time_slot, season")
    .order("day", { ascending: true })
    .order("time_slot", { ascending: true });

  const { data, error } = await query;
  if (error) throw error;

  const rows = (data ?? []).filter(
    (row) => !season || row.season === season.name || row.season === "evergreen"
  );

  return rows.map((row) => ({
    day: row.day as PracticeSlot["day"],
    team: row.team,
    time: row.time_slot,
    timeSlot: row.time_slot,
  }));
}

export async function getPlaybookResources(): Promise<PlaybookResources> {
  const [resourceRes, videosRes] = await Promise.all([
    supabase
      .from("playbook_resources")
      .select(
        "playbook_pdf_url, playbook_printable_pdf_url, playbook_preview_image, greenlight_pdf_url, greenlight_preview_image"
      )
      .limit(1)
      .single(),
    supabase
      .from("playbook_videos")
      .select("title, youtube_url, thumbnail, is_external")
      .order("sort_order", { ascending: true }),
  ]);

  if (resourceRes.error || !resourceRes.data) {
    throw resourceRes.error ?? new Error("No playbook_resources row found");
  }
  if (videosRes.error) throw videosRes.error;

  const youtubeVideos: YouTubeVideo[] = (videosRes.data ?? []).map((v) => ({
    title: v.title,
    youtubeUrl: v.youtube_url,
    thumbnail: v.thumbnail,
    isExternal: v.is_external ?? undefined,
  }));

  return {
    playbookPdfUrl: resourceRes.data.playbook_pdf_url,
    playbookPrintablePdfUrl:
      resourceRes.data.playbook_printable_pdf_url ?? resourceRes.data.playbook_pdf_url,
    playbookPreviewImage: resourceRes.data.playbook_preview_image,
    greenlightShootingPdfUrl: resourceRes.data.greenlight_pdf_url,
    greenlightPreviewImage: resourceRes.data.greenlight_preview_image,
    youtubeVideos,
  };
}

export async function getHallOfFamePlayers(): Promise<HallOfFamePlayer[]> {
  const { data, error } = await supabase
    .from("hall_of_fame_players")
    .select("id, name, college, high_school, sport, slug, image_url, sort_order")
    .order("sort_order", { ascending: true });

  if (error) throw error;

  return (data ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    college: row.college,
    highSchool: row.high_school ?? "",
    sport: row.sport,
    slug: row.slug,
    image: row.image_url,
  }));
}
